import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import {
  ALL_AUDIT_ACTIONS,
  ALL_AUDIT_TARGET_TYPES,
  type AuditAction,
  type AuditTargetType,
} from '@etiquetachef/types';

const ALL_VALUE = '__ALL__';

type Props = {
  action: AuditAction | null;
  targetType: AuditTargetType | null;
  targetId: string;
  onChangeAction: (action: AuditAction | null) => void;
  onChangeTargetType: (type: AuditTargetType | null) => void;
  onChangeTargetId: (id: string) => void;
};

/**
 * Filtros da página `/audit`: ação, tipo de alvo e id do alvo (controlados).
 *
 * Origem: sem issue — commit 05030f0d1, 2026-04-26 17:26 +0200 (aba Audit do
 * tenant + página /audit global, Sprint 2).
 *
 * Contrato: componente controlado; "Todos" vira `null`. As opções vêm de
 * `ALL_AUDIT_ACTIONS`/`ALL_AUDIT_TARGET_TYPES` (`@etiquetachef/types`) e são
 * exibidas pelo valor cru (sem tradução). O texto do id não é validado.
 *
 * Usado por (admin): `routes/AuditPage.tsx`.
 *
 * Dicas: toda combinação de filtros que a página montar precisa de índice em
 * `firebase/firestore.indexes.json` — ver `subscribeAuditEvents`.
 *
 * Histórico:
 *  - 2026-10-10 15:12 +0200 — (#936) cabeçalho padrão (#796).
 */
export function AuditFilters(props: Props): React.JSX.Element {
  const { t } = useTranslation();
  const { action, targetType, targetId, onChangeAction, onChangeTargetType, onChangeTargetId } =
    props;

  const actionOptions = [
    { value: ALL_VALUE, label: t('common.all') },
    ...ALL_AUDIT_ACTIONS.map((a) => ({ value: a, label: a })),
  ];
  const targetTypeOptions = [
    { value: ALL_VALUE, label: t('common.all') },
    ...ALL_AUDIT_TARGET_TYPES.map((tt) => ({ value: tt, label: tt })),
  ];

  return (
    <SpaceBetween direction="horizontal" size="s">
      <FormField label={t('audit.filters.action')}>
        <Select
          ariaLabel={t('audit.filters.action')}
          selectedOption={actionOptions.find((o) => o.value === (action ?? ALL_VALUE)) ?? null}
          options={actionOptions}
          onChange={({ detail }): void => {
            const v = detail.selectedOption.value;
            onChangeAction(v === ALL_VALUE ? null : (v as AuditAction));
          }}
        />
      </FormField>

      <FormField label={t('audit.filters.targetType')}>
        <Select
          ariaLabel={t('audit.filters.targetType')}
          selectedOption={targetTypeOptions.find((o) => o.value === (targetType ?? ALL_VALUE)) ?? null}
          options={targetTypeOptions}
          onChange={({ detail }): void => {
            const v = detail.selectedOption.value;
            onChangeTargetType(v === ALL_VALUE ? null : (v as AuditTargetType));
          }}
        />
      </FormField>

      <FormField label={t('audit.filters.targetId')} description={t('audit.filters.note')}>
        <Input
          value={targetId}
          placeholder={t('audit.filters.targetIdPlaceholder')}
          onChange={({ detail }): void => {
            onChangeTargetId(detail.value);
          }}
        />
      </FormField>
    </SpaceBetween>
  );
}
